import { Pencil, Trash } from "lucide-react";
import {
  ShowBase,
  useDeleteController,
  useShowContext,
  useTranslate,
  type ShowBaseProps,
} from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { Sponsoring } from "../types";
import { SponsoringShowContent } from "./SponsoringShow";

/**
 * Mobile show page. It renders the same body as the desktop show dialog
 * (company, badges, details, company block and notes); the page actions
 * "Bewerken" (opens the edit dialog on `${basePath}/:id`) and "Verwijderen"
 * live in the fixed header.
 */
export const SponsoringShowMobile = ({
  basePath = "/sponsorings",
  ...props
}: ShowBaseProps & {
  /** The overview this page belongs to (/sponsorings, /balsponsoren, /leads) */
  basePath?: string;
} = {}) => (
  <ShowBase
    queryOptions={{
      onError: () => {
        /* Disable error notification: the content handles the offline case */
      },
    }}
    {...props}
  >
    <SponsoringShowContentMobile basePath={basePath} />
  </ShowBase>
);

const SponsoringShowContentMobile = ({ basePath }: { basePath: string }) => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Sponsoring>();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to={basePath} />
        <Link to={basePath} className="flex-1 min-w-0">
          <h1 className="truncate text-xl font-semibold text-brand-navy">
            {translate("resources.sponsorings.name", { smart_count: 1 })}
          </h1>
        </Link>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            asChild
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
          >
            <Link to={`${basePath}/${record.id}`}>
              <Pencil className="size-5" />
              <span className="sr-only">
                {translate("resources.sponsorings.action.edit")}
              </span>
            </Link>
          </Button>
          <SponsoringDeleteIconButton redirect={basePath} />
        </div>
      </MobileHeader>
      <MobileContent>
        <SponsoringShowContent hideActions basePath={basePath} />
      </MobileContent>
    </>
  );
};

/**
 * Icon-only variant of the DeleteButton for the mobile page header; the
 * delete stays undoable, exactly like on the desktop dialog.
 */
const SponsoringDeleteIconButton = ({ redirect }: { redirect: string }) => {
  const translate = useTranslate();
  const { isPending, handleDelete } = useDeleteController({
    mutationMode: "undoable",
    redirect,
  });

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={isPending}
      onClick={handleDelete}
      className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
    >
      <Trash className="size-5" />
      <span className="sr-only">
        {translate("resources.sponsorings.action.delete")}
      </span>
    </Button>
  );
};
